import React, { useState, useEffect } from 'react';
import { FaTimes, FaUpload } from 'react-icons/fa';

const API_URL = '/api';

const initialFields = {
  product_code: '', product_name: '', product_category_id: '', uom_name: '', status: 'Active',
  description: '', product_image: null, created_by: '', updated_by: ''
};

const ProductFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
  const [fields, setFields] = useState({ ...initialFields, product_code: 'Loading...' });
  const [errors, setErrors] = useState({});
  const [isLoadingCode] = useState(false);
  const [categories, setCategories] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingUoms, setIsLoadingUoms] = useState(true);
  const [filePreview, setFilePreview] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Fetch categories
  useEffect(() => {
    setIsLoadingCategories(true);
    fetch(`${API_URL}/product-categories`)
      .then(res => res.json())
      .then(data => setCategories(data))
      .catch(error => console.error('Error fetching categories:', error))
      .finally(() => setIsLoadingCategories(false));
  }, []);

  // Fetch UOMs
  useEffect(() => {
    setIsLoadingUoms(true);
    fetch(`${API_URL}/uoms`)
      .then(res => res.json())
      .then(data => setUoms(data))
      .catch(error => console.error('Error fetching UOMs:', error))
      .finally(() => setIsLoadingUoms(false));
  }, []);

  // Reset form state on close
  useEffect(() => {
    if (!open) {
      setFields(initialFields);
      setErrors({});
      setFilePreview({});
      setLoadingDetails(false);
    }
  }, [open]);

  // Fetch product details or next code on open
  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setLoadingDetails(true);
        fetch(`${API_URL}/products/${initialData}`)
          .then(res => res.json())
          .then(data => {
            setFields({
              product_code: data.ProductCode || '',
              product_name: data.ProductName || '',
              product_category_id: data.ProductCategoryID || '',
              uom_name: data.UOMName || '',   // ✅ using name
              status: data.Status || 'Active',
              description: data.Description || '',
              product_image: null,
              created_by: data.CreatedBy || '',
              updated_by: data.UpdatedBy || ''
            });
            setFilePreview({});
          })
          .catch(error => console.error('Error fetching product details:', error))
          .finally(() => setLoadingDetails(false));
      } else {
        // Show form immediately, fetch code in background
        setFields(prev => ({ ...initialFields, product_code: 'Loading...' }));
        fetch(`${API_URL}/productlatestcode`)
          .then(res => res.json())
          .then(data => setFields(prev => ({ ...prev, product_code: data.nextCode })))
          .catch(error => {
            console.error('Error fetching latest product code:', error);
            setFields(prev => ({ ...prev, product_code: 'P-01' }));
          });
      }
      setErrors({});
    }
  }, [open, initialData, isEdit]);

  const handleChange = e => {
    setFields({ ...fields, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setFields(prev => ({ ...prev, [fieldName]: file }));
      setFilePreview(prev => ({ ...prev, [fieldName]: URL.createObjectURL(file) }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!fields.product_name) errs.product_name = 'Required';
    if (!fields.product_category_id) errs.product_category_id = 'Required';
    if (!fields.uom_name) errs.uom_name = 'Required';   // ✅ validate name
    if (!fields.status) errs.status = 'Required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return; // Prevent double submission
    if (!validate()) return;
    const formData = new FormData();
    Object.keys(fields).forEach(key => {
      if (key !== 'product_image') {
        formData.append(key, fields[key]);
      }
    });
    if (fields.product_image) {
      formData.append('product_image', fields.product_image);
    }
    if (isEdit) {
      formData.set('updated_by', 1); // Replace 1 with current user ID if available
    } else {
      formData.set('created_by', 1);
    }
    onSubmit(formData);
  };

  if (!open) return null;

  if (loadingDetails || isLoadingCode) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <span className="text-[#2b6777] text-lg font-semibold">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40">
      <div className="bg-[#E5E1DA] rounded-2xl shadow-2xl w-full max-w-5xl p-4 sm:p-8 relative overflow-y-auto max-h-[95vh]">
        <button className="absolute top-4 right-4 text-[#2b6777] hover:text-[#52ab98]" onClick={onClose}>
          <FaTimes size={20} />
        </button>
        
        <h2 className="text-xl sm:text-2xl font-bold text-[#2b6777] mb-4 sm:mb-6 text-center">
          {isEdit ? 'Edit Product' : 'Create New Product'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Product Information Section */}
          <div className='bg-white p-3 sm:p-4 rounded-xl shadow-md'>
            <h3 className="text-base sm:text-lg font-bold text-[#2b6777] mb-2">Product Information</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
              
              {/* Product Code */}
              <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
                <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:mb-0 sm:w-1/4">Product Code</label>
                <input name="product_code" value={fields.product_code} readOnly 
                  className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-700" />
              </div>
              
              {/* Product Name */}
              <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
                <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:w-1/4">Product Name *</label>
                <div className="w-full sm:w-3/4">
                  <input name="product_name" value={fields.product_name} onChange={handleChange}
                    className={`w-full border rounded px-3 py-2 ${errors.product_name ? 'border-red-400' : 'border-[#c8d8e4]'}`} />
                  {errors.product_name && <span className="text-xs text-red-500">{errors.product_name}</span>}
                </div>
              </div>
              
              {/* Category */}
              <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
                <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:w-1/4">Category *</label>
                <div className="w-full sm:w-3/4">
                  <select name="product_category_id" value={fields.product_category_id} onChange={handleChange}
                    className={`w-full border rounded px-3 py-2 ${errors.product_category_id ? 'border-red-400' : 'border-[#c8d8e4]'}`}
                    disabled={isLoadingCategories}>
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category.CategoryID} value={category.CategoryID}>{category.CategoryName}</option>
                    ))}
                  </select>
                  {errors.product_category_id && <span className="text-xs text-red-500">{errors.product_category_id}</span>}
                </div>
              </div>
              
              {/* UOM (by name) */}
              <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
                <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:w-1/4">UOM *</label>
                <div className="w-full sm:w-3/4">
                  <select name="uom_name" value={fields.uom_name} onChange={handleChange}
                    className={`w-full border rounded px-3 py-2 ${errors.uom_name ? 'border-red-400' : 'border-[#c8d8e4]'}`}
                    disabled={isLoadingUoms}>
                    <option value="">Select UOM</option>
                    {uoms.map(uom => (
                      <option key={uom.UOMID} value={uom.UOMName}>{uom.UOMName}</option>
                    ))}
                  </select>
                  {errors.uom_name && <span className="text-xs text-red-500">{errors.uom_name}</span>}
                </div>
              </div>
              
              {/* Status */}
              <div className='flex flex-col sm:flex-row sm:gap-2 sm:items-center'>
                <label className="block text-sm font-medium text-[#2b6777] mb-1 sm:w-1/4">Status *</label>
                <div className="w-full sm:w-3/4">
                  <select name="status" value={fields.status} onChange={handleChange}
                    className={`w-full border rounded px-3 py-2 ${errors.status ? 'border-red-400' : 'border-[#c8d8e4]'}`}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                  {errors.status && <span className="text-xs text-red-500">{errors.status}</span>}
                </div>
              </div>
            </div>
          </div>
          
          {/* Description Section */}
          <div className='bg-white p-3 sm:p-4 rounded-xl shadow-md'>
            <h3 className="text-base sm:text-lg font-bold text-[#2b6777] mb-2">Description</h3>
            <textarea name="description" value={fields.description} onChange={handleChange}
              className="w-full border border-[#c8d8e4] rounded px-3 py-2 min-h-[60px]"
              placeholder="Enter a short description of the product" />
            
            {/* Product Image */}
            <div className='mt-4'>
              <label className="block text-sm font-medium text-[#2b6777]">Product Image (Optional)</label>
              <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'product_image')}
                className="hidden" id="product-image-upload" />
              <label htmlFor="product-image-upload"
                className="flex items-center gap-2 px-4 py-2 bg-[#52ab98] text-white rounded cursor-pointer hover:bg-[#2b6777] w-fit mt-2">
                <FaUpload /> Upload Product Image
              </label>
              {filePreview.product_image && <img src={filePreview.product_image} alt="preview" className="w-10 h-10 mt-2 rounded" />}
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <button type="button" className="px-4 py-2 rounded bg-gray-200 text-[#2b6777]" onClick={onClose}>Cancel</button>
            <button type="submit" className="px-6 py-2 rounded bg-[#52ab98] hover:bg-[#2b6777] text-white"
              disabled={loading || fields.product_code === 'Loading...'}>
              {loading ? 'Saving...' : (isEdit ? 'Update Product' : 'Create Product')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductFormModal;
